import React, { useState, useEffect } from 'react';

import { createPortal } from 'react-dom';

import { useEntitlement } from '@/hooks/use-entitlement';
import { useAuthGate } from '@/components/auth/auth-gate';
import { useUpgrade } from '@/components/billing/premium-lock';
import TrackerTutorialGuide from '@/components/tracker360/TrackerTutorialGuide';
import Tracker360Landing from '@/components/tracker360/Tracker360Landing';

import LectureMoveModal from '@/components/tracker360/LectureMoveModal';

import YesterdayPendingBanner from '@/components/tracker360/YesterdayPendingBanner';

import SundayRevisionCard from '@/components/tracker360/SundayRevisionCard';

import {
  applyMove,
  todayISO,
  prettyDate,
  yesterdayISO,
  acknowledgePending,
  pendingAcknowledgedFor,
  MAX_MOVES_PER_DAY,
} from '@/lib/tracker360-moves';

import { 

  Upload, CheckCircle, Clock, BookOpen, AlertCircle, 

  FileText, ArrowRight, RotateCcw, Atom, FlaskConical, 

  Calculator, Dna, TrendingUp, Calendar, CheckSquare, 

  Sparkles, Lock, Crown, Zap, X, ShieldCheck, CalendarClock,

  Flame, Target, ChevronRight

} from 'lucide-react';
import bannerImg from '@/assets/tracker360-banner.jpg';

const FALLBACK_BADGE = 'bg-amber-500/10 border-amber-500/30 text-amber-700 dark:bg-lime-400/10 dark:border-lime-400/25 dark:text-lime-300';

const SUBJECT_CONFIG = {

  Physics: { icon: Atom, color: 'text-amber-600 dark:text-lime-400', badge: 'bg-amber-500/10 border-amber-500/30 text-amber-700 dark:bg-lime-400/10 dark:border-lime-400/25 dark:text-lime-300' },

  Chemistry: { icon: FlaskConical, color: 'text-emerald-600 dark:text-emerald-400', badge: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:bg-emerald-400/10 dark:border-emerald-400/25 dark:text-emerald-300' },

  Maths: { icon: Calculator, color: 'text-orange-600 dark:text-lime-300', badge: 'bg-orange-500/10 border-orange-500/30 text-orange-700 dark:bg-lime-300/10 dark:border-lime-300/25 dark:text-lime-200' },

  Biology: { icon: Dna, color: 'text-emerald-600 dark:text-emerald-300', badge: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:bg-emerald-300/10 dark:border-emerald-300/25 dark:text-emerald-200' }

};

function subjectMeta(name) {

  return SUBJECT_CONFIG[name] || { icon: BookOpen, color: 'text-amber-600 dark:text-lime-300', badge: FALLBACK_BADGE };

}


// =====================================================
// TRACKER 360 — FREE / PREMIUM ACCESS CONFIG
// Connect this with the EXISTING subscription + Cashfree system.
// DO NOT create a new payment gateway or subscription system.
// =====================================================

const TRACKER_360_ACCESS = {

  free: {

    canCreateTracker: true,

    canUploadJSON: true,

    canSelectChapters: true,

    canGenerateSchedule: true,

    canViewFullSchedule: true,

    canViewFutureLectures: true,

    // FREE USER CAN COMPLETE ONLY FIRST 7 SCHEDULE DAYS

    maxCompletionDay: 2,

    canCompleteAfterDay7: false,

  },

  premium: {

    canCreateTracker: true,

    canUploadJSON: true,

    canSelectChapters: true,

    canGenerateSchedule: true,

    canViewFullSchedule: true,

    canViewFutureLectures: true,

    // PREMIUM = UNLIMITED

    maxCompletionDay: Infinity,

    canCompleteAfterDay7: true,

  }

};

function getTracker360Access(isPremium) {

  return isPremium

    ? TRACKER_360_ACCESS.premium

    : TRACKER_360_ACCESS.free;

}

function canCompleteTrackerLecture({

  isPremium,

  scheduleDay

}) {

  if (isPremium === true) {

    return {

      allowed: true,

      reason: "premium"

    };

  }

  if (Number(scheduleDay) <= 2) {

    return {

      allowed: true,

      reason: "free_7_day_access"

    };

  }

  return {

    allowed: false,

    reason: "premium_required"

  };

}

const TRACKER_360_PREMIUM_POPUP = {

  badge: "TRACKER 360 PRO",

  title: "Your Roadmap Is Ready 🚀",

  description:

    "Explore your complete Tracker 360 roadmap for free. " +

    "Complete the first 2 days and unlock unlimited lecture completion with Premium.",

  benefits: [

    "Complete lectures beyond Day 2",

    "Execute your complete study roadmap",

    "Track your entire backlog",

    "Follow your complete day-wise schedule",

    "Unlimited Tracker 360 completion"

  ],

  primaryButton: "Explore Premium",

  secondaryButton: "Continue Exploring"

};

function handleTracker360Completion({

  isPremium,

  scheduleDay,

  openPremiumModal,

  completeLecture

}) {

  const access = canCompleteTrackerLecture({

    isPremium,

    scheduleDay

  });

  if (access.allowed) {

    return completeLecture();

  }

  openPremiumModal(TRACKER_360_PREMIUM_POPUP);

  return {

    success: false,

    locked: true,

    reason: "premium_required"

  };

}

function getTracker360Badge(isPremium) {

  if (isPremium) {

    return {

      text: "PREMIUM — FULL ACCESS",

      type: "premium"

    };

  }

  return {

    text: "FREE — 2 DAY ACCESS",

    type: "free"

  };

}

const TRACKER_360_FREE_INFO = {

  title: "2-Day Free Access",

  message:

    "Build your complete Tracker 360 roadmap and explore your future schedule. " +

    "Lecture completion is free for the first 2 schedule days.",

  lockedMessage:

    "This lecture is beyond your 2-day free access. Upgrade to Premium to continue completing your roadmap.",

  unlockedDays: "1–2",

  lockedDays: "3+"

};

function openTracker360Premium(openUpgradeFn) {

  if (typeof openUpgradeFn === "function") {

    openUpgradeFn();

    return;

  }

  if (typeof window !== "undefined") {

    if (typeof window.openSubscriptionModal === "function") {

      window.openSubscriptionModal();

      return;

    }

    if (typeof window.openPremiumPlans === "function") {

      window.openPremiumPlans();

      return;

    }

  }

}

const TRACKER_360_FEATURES = {

  createTracker: {

    free: true,

    premium: true

  },

  uploadJSON: {

    free: true,

    premium: true

  },

  selectChapters: {

    free: true,

    premium: true

  },

  generateSchedule: {

    free: true,

    premium: true

  },

  viewCompleteSchedule: {

    free: true,

    premium: true

  },

  viewFutureLectures: {

    free: true,

    premium: true

  },

  completeDay1To7: {

    free: true,

    premium: true

  },

  completeDay8Plus: {

    free: false,

    premium: true

  }

};

export default function Tracker360() {

  const [showLanding, setShowLanding] = useState(true);

  const { isAuthenticated, requireAuth } = useAuthGate();

  const [step, setStep] = useState(1);

  const [selectedClass, setSelectedClass] = useState('11');

  const [subjectPlanners, setSubjectPlanners] = useState({});

  const [activeUploadSubject, setActiveUploadSubject] = useState('Physics');

  const [selectedChapters, setSelectedChapters] = useState([]);

  const [activeChapterSubject, setActiveChapterSubject] = useState(null);

  const [targetDays, setTargetDays] = useState(60); // Max up to 1460 (4 Years)

  const [frequency, setFrequency] = useState(1); // 1 = every day, 2 = every 2nd day, ...


  const [playbackSpeed, setPlaybackSpeed] = useState(1.5);

  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);

  const [generatedSchedule, setGeneratedSchedule] = useState([]);

  const [uploadError, setUploadError] = useState('');

  // Subscription & Paywall States

  const { entitlement } = useEntitlement();

  const { openUpgrade } = useUpgrade();

  const isPremiumUser = entitlement?.paid_active === true;

  const [showPaywallModal, setShowPaywallModal] = useState(false);

  const [paywallReason, setPaywallReason] = useState('');

  // Custom subjects (UI only) — PCMB stays the default primary group

  const [customSubjects, setCustomSubjects] = useState([]);

  const [showAddSubject, setShowAddSubject] = useState(false);

  const [newSubjectName, setNewSubjectName] = useState('');

  // Lecture move / reschedule + yesterday's pending work
  const [moveTarget, setMoveTarget] = useState(null); // { lecture, fromDate, preselectDate }
  const [pendingAckAt, setPendingAckAt] = useState(0);
  const [limitNotice, setLimitNotice] = useState('');

  // Sunday Test & Revision checklist completion, keyed by schedule date (ISO).
  const [sundayChecks, setSundayChecks] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('quantum_tracker360_sunday_checks') || '{}');
    } catch {
      return {};
    }
  });

  const toggleSundayCheck = (date, key) => {
    setSundayChecks((prev) => {
      const next = {
        ...prev,
        [date]: { ...(prev[date] || {}), [key]: !(prev[date] || {})[key] },
      };
      localStorage.setItem('quantum_tracker360_sunday_checks', JSON.stringify(next));
      return next;
    });
  };

  const allSubjects = [...Object.keys(SUBJECT_CONFIG), ...customSubjects];

  const handleAddSubject = () => {

    const name = newSubjectName.trim();

    if (!name) return;

    if (!allSubjects.some((s) => s.toLowerCase() === name.toLowerCase())) {

      setCustomSubjects((prev) => [...prev, name]);

    }

    setActiveUploadSubject(name);

    setNewSubjectName('');

    setShowAddSubject(false);

  };


  // 1. Auto-Load from LocalStorage on mount

  useEffect(() => {

    const savedData = localStorage.getItem('quantum_tracker360_data');

    if (savedData) {

      try {

        const parsed = JSON.parse(savedData);

        if (parsed.generatedSchedule && parsed.generatedSchedule.length > 0) {

          setGeneratedSchedule(parsed.generatedSchedule);

          setSelectedClass(parsed.selectedClass || '11');

          setTargetDays(parsed.targetDays || 60);

          setFrequency(parsed.frequency || 1);


          setPlaybackSpeed(parsed.playbackSpeed || 1.5);

          setStep(5);

          setShowLanding(false);

        }

      } catch (e) {

        console.error("Storage loading error", e);

      }

    }

  }, []);

  // 2. Save Schedule & Progress to LocalStorage

  const persistSchedule = (newSchedule) => {

    setGeneratedSchedule(newSchedule);

    localStorage.setItem('quantum_tracker360_data', JSON.stringify({

      generatedSchedule: newSchedule,

      selectedClass,

      targetDays,

      frequency,


      playbackSpeed,

      updatedAt: new Date().toISOString()

    }));

  };

  // Robust Subject-wise JSON Parser

  const handleFileUpload = (event) => {

    const file = event.target.files?.[0];

    setUploadError('');

    if (!file) return;

    const reader = new FileReader();

    reader.onload = (e) => {

      try {

        const raw = JSON.parse(e.target.result);

        let extractedChapters = [];

        const rootClasses = Array.isArray(raw.classes) ? raw.classes : (raw.subjects?.[0]?.classes || []);

        

        if (rootClasses.length > 0) {

          const targetClassObj = rootClasses.find((c) => {

            const name = (c.name || c.id || '').toLowerCase();

            return name.includes(selectedClass.toLowerCase());

          }) || rootClasses[0];

          extractedChapters = (targetClassObj.chapters || []).map((ch, idx) => ({

            id: ch.id || `${activeUploadSubject}_${idx}`,

            name: ch.name || `Chapter ${idx + 1}`,

            totalLectures: Number(ch.lectureCount || ch.totalLectures || (ch.lectures ? ch.lectures.length : 4)),

            lectures: ch.lectures || [],

            subject: activeUploadSubject

          }));

        } else if (raw.chapters && Array.isArray(raw.chapters)) {

          extractedChapters = raw.chapters.map((ch, idx) => ({

            id: ch.id || `${activeUploadSubject}_${idx}`,

            name: ch.name || `Chapter ${idx + 1}`,

            totalLectures: Number(ch.lectureCount || ch.totalLectures || (ch.lectures ? ch.lectures.length : 4)),

            lectures: ch.lectures || [],

            subject: activeUploadSubject

          }));

        }

        if (extractedChapters.length === 0) {

          setUploadError(`Is JSON me ${activeUploadSubject} ke chapters nahi mile.`);

          return;

        }

        setSubjectPlanners(prev => ({

          ...prev,

          [activeUploadSubject]: extractedChapters

        }));

        setSelectedChapters(prev => [

          ...prev.filter(c => c.subject !== activeUploadSubject),

          ...extractedChapters

        ]);

      } catch (err) {

        setUploadError('Invalid JSON format. Kripya valid file upload karein.');

      }

    };

    reader.readAsText(file);

  };

  const toggleChapter = (chapter) => {

    const exists = selectedChapters.find((c) => c.id === chapter.id);

    if (exists) {

      setSelectedChapters(selectedChapters.filter((c) => c.id !== chapter.id));

    } else {

      setSelectedChapters([...selectedChapters, chapter]);

    }

  };

  // Pace calculations (Excludes mock tests & revision Sundays)

  const totalLecturesCount = selectedChapters.reduce((acc, c) => acc + c.totalLectures, 0);

  // A day is a lecture-study day only when it matches the chosen frequency and
  // is neither a Sunday (revision) nor a 14th-day mock test.
  const isLectureDay = (dayCount, date) =>
    (dayCount - 1) % frequency === 0 && dayCount % 14 !== 0 && date.getDay() !== 0;

  const effectiveStudyDays = (() => {
    const cursor = new Date(startDate);
    let count = 0;
    for (let d = 1; d <= targetDays; d++) {
      if (isLectureDay(d, cursor)) count += 1;
      cursor.setDate(cursor.getDate() + 1);
    }
    return Math.max(1, count);
  })();

  const calculatedDailyRate = Math.ceil(totalLecturesCount / effectiveStudyDays) || 1;

  // Feasibility: a realistic study day caps at ~8 hours of lectures.
  const lectureMinutes = Math.round(120 / playbackSpeed);
  const maxLecturesPerDay = Math.max(1, Math.floor(480 / lectureMinutes));
  const frequencyOverloaded = totalLecturesCount > 0 && calculatedDailyRate > maxLecturesPerDay;


  // Schedule Engine with 14th-Day Mock Test & Subject Interleaving

  const buildSchedule = () => {

    const queuesBySubject = {};

    selectedChapters.forEach((ch) => {

      if (!queuesBySubject[ch.subject]) queuesBySubject[ch.subject] = [];

      for (let i = 1; i <= ch.totalLectures; i++) {

        queuesBySubject[ch.subject].push({

          chapterName: ch.name,

          subject: ch.subject,

          lectureNumber: i,

          totalLecturesInChapter: ch.totalLectures,

          topic: ch.lectures?.[i - 1]?.topic || `Lecture ${i}`,

          effectiveDuration: Math.round(120 / playbackSpeed),

          dpp: `DPP #${i} • ${ch.name}`,

          completed: false,

          id: `${ch.id}_lec_${i}`

        });

      }

    });

    const combinedQueue = [];

    const subjects = Object.keys(queuesBySubject);

    let hasMore = true;

    while (hasMore) {

      hasMore = false;

      subjects.forEach((sub) => {

        if (queuesBySubject[sub].length > 0) {

          combinedQueue.push(queuesBySubject[sub].shift());

          hasMore = true;

        }

      });

    }

    const schedule = [];

    let curDate = new Date(startDate);

    let dayCount = 1;

    while (combinedQueue.length > 0 && dayCount <= targetDays + 60) {

      const isSunday = curDate.getDay() === 0;

      const is14thDay = dayCount % 14 === 0;

      if (is14thDay) {

        schedule.push({

          dayNumber: dayCount,

          date: curDate.toISOString().split('T')[0],

          isOff: true,

          type: 'TEST',

          title: '🔥 14th Day Major Mock Test & Backlog Analysis',

          description: 'Zero lectures today. Revise covered syllabus & attempt full length mock test.',

          lectures: []

        });

      } else if (isSunday) {

        schedule.push({

          dayNumber: dayCount,

          date: curDate.toISOString().split('T')[0],

          isOff: true,

          type: 'REVISION',

          title: '⚡ Sunday Revision & Pending DPP Clearing',

          description: 'Clear backlogs from the past week and revise short formulas.',

          lectures: []

        });

      } else if ((dayCount - 1) % frequency !== 0) {

        schedule.push({

          dayNumber: dayCount,

          date: curDate.toISOString().split('T')[0],

          isOff: true,

          type: 'GAP',

          title: '🌙 Rest / Gap Day (Schedule Frequency)',

          description: 'No new lectures today as per your selected schedule frequency. Use it to rest or revise.',

          lectures: []

        });

      } else {


        const todayLectures = combinedQueue.splice(0, calculatedDailyRate);

        schedule.push({

          dayNumber: dayCount,

          date: curDate.toISOString().split('T')[0],

          isOff: false,

          type: 'STUDY',

          title: `Day ${dayCount} Multi-Subject Targets`,

          lectures: todayLectures

        });

      }

      curDate.setDate(curDate.getDate() + 1);

      dayCount++;

    }

    persistSchedule(schedule);

    setStep(5);

  };

  // 7-Day Paywall Check on Marking Completed

  const openPremiumModal = (popupData) => {

    setPaywallReason(popupData?.description || TRACKER_360_PREMIUM_POPUP.description);

    setShowPaywallModal(true);

  };

  const toggleLectureStatus = (dayIndex, lecId, visibleIdx) => {

    const targetDay = generatedSchedule[dayIndex];

    // Defensive: missing/invalid day data must never throw — open popup if locked, else no-op.

    if (!targetDay || !Number.isFinite(Number(targetDay.dayNumber))) {

      return;

    }

    // Gate BEFORE any mutation: free users clicking calendar Day 8+ only open the premium popup.

    if (!isPremiumUser && Number(targetDay.dayNumber) > 2) {

      openPremiumModal({

        description:

          "Your free 2-day access has ended. Upgrade to Premium to complete lectures beyond Day 2."

      });

      return;

    }

    // Daily mark lock: future-day lectures cannot be marked early.
    const _lec = targetDay.lectures?.find((l) => l.id === lecId);
    if (_lec && !_lec.completed && targetDay.date && targetDay.date > todayISO()) {
      return;
    }

    const completeLecture = () => {

      const updated = [...generatedSchedule];

      const lec = updated[dayIndex]?.lectures?.find((l) => l.id === lecId);

      if (lec) {

        lec.completed = !lec.completed;

        persistSchedule(updated);

      }

      return { success: true };

    };

    handleTracker360Completion({

      isPremium: isPremiumUser,

      scheduleDay: visibleIdx,

      openPremiumModal,

      completeLecture

    });

  };


  /* ---------- Lecture move / reschedule ---------- */

  const openMoveModal = (lecture, fromDate, preselectDate) => {
    setMoveTarget({ lecture, fromDate, preselectDate });
  };

  const confirmMove = (toDate) => {
    if (!moveTarget) return;
    const result = applyMove(generatedSchedule, moveTarget.lecture.id, moveTarget.fromDate, toDate);
    if (result.ok) {
      persistSchedule(result.schedule);
      setMoveTarget(null);
      return;
    }
    setMoveTarget(null);
    if (result.reason === 'daily-limit') {
      setLimitNotice(`Daily Move Limit Reached — you can move up to ${MAX_MOVES_PER_DAY} lectures per day.`);
    } else if (result.reason === 'lecture-limit') {
      setLimitNotice("Move limit reached — you've already rescheduled this lecture 3 times.");
    } else {
      setLimitNotice('That date is no longer available for this lecture.');
    }
  };

  /* ---------- Yesterday's pending work ---------- */

  const yesterday = yesterdayISO();
  const yesterdayDay = generatedSchedule.find((d) => d.date === yesterday && !d.isOff);
  const yesterdayPending = (yesterdayDay?.lectures || []).filter((l) => !l.completed);
  const showPendingBanner =
    yesterdayPending.length > 0 && !pendingAcknowledgedFor(yesterday) && pendingAckAt >= 0;

  const dismissPending = () => {
    acknowledgePending(yesterday);
    setPendingAckAt(Date.now());
  };

  const markYesterdayDone = () => {

    if (!yesterdayDay) return;

    const yesterdayVisibleIdx = getVisibleIndex(yesterdayDay.dayNumber);

    if (!isPremiumUser && Number(yesterdayDay.dayNumber) > 2) {

      openPremiumModal({

        description:

          'Your free 2-day access has ended. Upgrade to Premium to complete lectures beyond Day 2.',

      });

      return;

    }

    const updated = generatedSchedule.map((day) =>

      day.date === yesterday

        ? { ...day, lectures: day.lectures.map((l) => ({ ...l, completed: true })) }

        : day,

    );

    persistSchedule(updated);

    dismissPending();

  };


  const handleReset = () => {

    localStorage.removeItem('quantum_tracker360_data');

    setGeneratedSchedule([]);

    setStep(1);

  };

  // Stats Analytics

  const totalRoadmapLectures = generatedSchedule.flatMap(d => d.lectures);

  const completedLecturesCount = totalRoadmapLectures.filter(l => l.completed).length;

  const progressPercent = totalRoadmapLectures.length > 0 

    ? Math.round((completedLecturesCount / totalRoadmapLectures.length) * 100) 

    : 0;

  // Visible timetable contains actual lecture-study days (no tests or gap days)
  // plus Sundays, which render as the dedicated Test & Revision Day card.

  const studyDays = generatedSchedule.filter((d) => !d.isOff && d.lectures.length > 0);

  let studyIdx = 0;

  const visibleSchedule = generatedSchedule

    .map((day, originalIdx) => ({ ...day, originalIdx }))

    .filter((day) => (!day.isOff && day.lectures.length > 0) || day.type === 'REVISION')

    .map((day) => ({ ...day, visibleIdx: day.type === 'REVISION' ? 0 : ++studyIdx }));

  const getVisibleIndex = (dayNumber) => {

    const idx = studyDays.findIndex((d) => d.dayNumber === dayNumber);

    return idx >= 0 ? idx + 1 : 0;

  };



  if (showLanding || !isAuthenticated) {

    return (
      <Tracker360Landing
        onStartTracking={() => requireAuth(() => setShowLanding(false)) && setShowLanding(false)}
      />
    );

  }

  return (

    <div className="w-full min-h-screen overflow-x-clip bg-amber-50/60 dark:bg-[var(--background)] text-slate-900 dark:text-[var(--foreground)] p-4 md:p-8 transition-colors duration-300">

      

      {/* Ambient Flowing Glow Background */}

      <div className="fixed inset-0 pointer-events-none overflow-hidden">

        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[520px] h-72 bg-amber-400/20 dark:bg-lime-400/[0.09] rounded-full blur-3xl" />

        <div className="absolute top-1/3 -right-32 w-96 h-96 bg-orange-400/10 dark:bg-emerald-500/[0.07] rounded-full blur-3xl" />

        <div className="absolute -bottom-32 left-0 w-96 h-96 bg-[var(--surface)]/40 dark:bg-[var(--surface)]/60 rounded-full blur-3xl" />


      </div>

      <div className="relative max-w-5xl mx-auto space-y-6">

        {/* Global Navigation Header */}

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 sm:p-6 rounded-3xl bg-white/70 dark:bg-white/[0.03] backdrop-blur-xl border border-slate-200 dark:border-white/10 shadow-xl shadow-slate-200/50 dark:shadow-[0_0_60px_-30px_rgba(154,205,75,0.45)]">

          <div>

            <div className="flex items-center gap-2.5">

              <span className="p-2 rounded-xl bg-amber-500/12 border border-amber-500/30 text-amber-600 dark:bg-lime-400/12 dark:border-lime-400/25 dark:text-lime-300">

                <Sparkles className="w-5 h-5" />

              </span>

              <h1 className="text-xl md:text-2xl font-extrabold tracking-tight text-slate-900 dark:text-[var(--foreground)]">

                Tracker 360 Pro

              </h1>

            </div>

            <p className="text-xs text-slate-500 dark:text-[var(--muted-foreground)] mt-1.5">

              PCMB Multi-Subject Backlog Execution • 14th-Day Mock Engine

            </p>

            <div className="mt-2.5">

              <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold border ${

                getTracker360Badge(isPremiumUser).type === 'premium'

                  ? 'bg-amber-500/10 border-amber-500/30 text-amber-700 dark:bg-lime-400/10 dark:border-lime-400/30 dark:text-lime-300'

                  : 'bg-white/5 border-white/15 text-slate-500 dark:text-[var(--muted-foreground)]'

              }`}>

                {getTracker360Badge(isPremiumUser).type === 'premium' ? <Crown className="w-3 h-3" /> : <Lock className="w-3 h-3" />}

                {getTracker360Badge(isPremiumUser).text}

              </span>

            </div>

          </div>

          {step === 5 ? (

            <div className="flex items-center gap-2">

              {!isPremiumUser && (

                <button

                  onClick={() => openTracker360Premium(() => openUpgrade(TRACKER_360_PREMIUM_POPUP.description))}

                  className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-[0_8px_24px_-10px_rgba(245,158,11,0.8)] hover:opacity-90 transition dark:from-lime-400 dark:to-emerald-400 dark:text-[#0a1206] dark:shadow-[0_8px_24px_-10px_rgba(154,205,75,0.8)]"

                >

                  <Crown className="w-3.5 h-3.5" /> Upgrade Pro

                </button>

              )}

              <button

                onClick={handleReset}

                className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold rounded-xl bg-white/5 border border-white/12 text-slate-600 dark:text-[var(--muted-foreground)] hover:border-amber-400/40 dark:hover:border-lime-400/25 transition"

              >

                <RotateCcw className="w-3.5 h-3.5" /> Reset Plan

              </button>

            </div>

          ) : (

            <div className="flex items-center gap-1.5 self-start px-3 py-1.5 rounded-full bg-slate-100 dark:bg-white/[0.04] border border-slate-200 dark:border-white/10 text-xs font-semibold text-slate-600 dark:text-[var(--muted-foreground)]">

              Step {step} of 4

            </div>

          )}


        </div>

        {/* Real-Time Analytics Bar (Visible when schedule is ready) */}

        {step === 5 && (() => {
          const today = todayISO();
          const todayDay = studyDays.find((d) => d.date === today);
          const todayTarget = todayDay ? todayDay.lectures.length : 0;
          // Display-only streak: consecutive fully-completed study days up to today
          let streak = 0;
          const past = studyDays.filter((d) => d.date && d.date <= today);
          for (let i = past.length - 1; i >= 0; i--) {
            const done = past[i].lectures.every((l) => l.completed);
            if (done) streak++;
            else if (past[i].date === today) continue;
            else break;
          }
          const stats = [
            { icon: <Zap className="w-3.5 h-3.5" />, value: `${calculatedDailyRate}`, label: 'Lec / Day' },
            { icon: <Calendar className="w-3.5 h-3.5" />, value: studyDays.length, label: 'Study Days' },
            { icon: <Flame className="w-3.5 h-3.5" />, value: streak, label: 'Streak' },
            { icon: <Target className="w-3.5 h-3.5" />, value: todayTarget, label: 'Today' },
          ];
          const handleClick = () => {
            if (!isPremiumUser) { openUpgrade(); return; }
            document.getElementById('tracker360-schedule')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          };
          return (
            <button
              type="button"
              onClick={handleClick}
              aria-label="Open Tracker 360 progress"
              className="group relative w-full overflow-hidden rounded-2xl border border-white/10 bg-[#0d100e] text-left shadow-[0_8px_24px_-12px_rgba(0,0,0,0.6)] transition-colors hover:border-white/20"
            >
              <img
                src={bannerImg}
                alt=""
                width={1536}
                height={640}
                loading="lazy"
                className="pointer-events-none absolute inset-0 h-full w-full object-cover object-right opacity-70"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#0d100e] via-[#0d100e]/85 to-[#0d100e]/20" />
              <div className="relative flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:gap-6 sm:p-5">
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Overall Progress</p>
                  <div className="mt-1 flex items-baseline gap-2 text-slate-100">
                    <span className="text-2xl font-bold">{progressPercent}%</span>
                    <span className="text-xs text-slate-400">• {completedLecturesCount} / {totalRoadmapLectures.length} Lectures</span>
                  </div>
                  <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
                    <div className="h-full rounded-full bg-amber-500 dark:bg-emerald-600 transition-all duration-500" style={{ width: `${progressPercent}%` }} />
                  </div>
                </div>
                <div className="flex items-center divide-x divide-white/10 sm:shrink-0">
                  {stats.map((s) => (
                    <div key={s.label} className="flex-1 px-2.5 first:pl-0 sm:flex-none sm:px-4">
                      <div className="flex items-center gap-1 text-sm font-semibold text-slate-100">
                        <span className="text-amber-400/80">{s.icon}</span>{s.value}
                      </div>
                      <p className="mt-0.5 whitespace-nowrap text-[10px] text-slate-400">{s.label}</p>
                    </div>
                  ))}
                </div>
                <ChevronRight className="absolute right-3 top-4 h-4 w-4 text-slate-400 transition-transform group-hover:translate-x-0.5 sm:static sm:h-5 sm:w-5" />
              </div>
            </button>
          );
        })()}

        {/* STEP 1: Class Selection & Subject JSON Feed Center */}

        {step === 1 && (

          <div className="space-y-5">

            <div className="p-5 sm:p-6 rounded-3xl bg-white/70 dark:bg-white/[0.03] backdrop-blur-xl border border-slate-200 dark:border-white/10 space-y-4">

              <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                1. Select Academic Category
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">

                {[
                  { id: '11', label: 'Class 11th' },
                  { id: '12', label: 'Class 12th' },
                  { id: 'dropper', label: 'Dropper Batch' }
                ].map((c) => (

                  <button
                    key={c.id}
                    onClick={() => setSelectedClass(c.id)}
                    className={`py-3.5 px-4 rounded-2xl text-xs font-semibold border transition-all ${
                      selectedClass === c.id
                        ? 'bg-amber-500/12 border-amber-500/40 text-amber-700 shadow-[0_0_24px_-8px_rgba(245,158,11,0.5)] dark:bg-lime-400/12 dark:border-lime-400/40 dark:text-lime-300 dark:shadow-[0_0_24px_-8px_rgba(154,205,75,0.6)]'
                        : 'bg-white/80 dark:bg-white/[0.02] border-amber-200/70 dark:border-white/10 text-slate-600 dark:text-slate-300 hover:border-amber-400/40 dark:hover:border-lime-400/25'
                    }`}
                  >
                    {c.label}
                  </button>

                ))}

              </div>

            </div>

            <div className="p-5 sm:p-6 rounded-3xl bg-white/70 dark:bg-white/[0.03] backdrop-blur-xl border border-slate-200 dark:border-white/10 space-y-4">

              <div className="flex flex-wrap items-center justify-between gap-2">

                <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                  2. Feed Subject Planners (JSON)
                </h2>

                <span className="text-[11px] text-slate-500 dark:text-slate-400">Upload one or more subjects</span>

              </div>

              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">

                {allSubjects.map((subKey) => {

                  const conf = subjectMeta(subKey);

                  const Icon = conf.icon;

                  const isUploaded = !!subjectPlanners[subKey];

                  const count = subjectPlanners[subKey]?.length || 0;

                  return (

                    <div
                      key={subKey}
                      onClick={() => setActiveUploadSubject(subKey)}
                      className={`p-4 rounded-2xl border cursor-pointer transition-all ${
                        activeUploadSubject === subKey
                          ? 'border-amber-500/50 bg-amber-500/[0.08] shadow-[0_0_26px_-10px_rgba(245,158,11,0.6)] dark:border-lime-400/40 dark:bg-lime-400/[0.07] dark:shadow-[0_0_26px_-10px_rgba(154,205,75,0.7)]'
                          : 'border-amber-200/70 dark:border-white/10 bg-white/70 dark:bg-white/[0.02] hover:border-amber-400/40 dark:hover:border-lime-400/20'
                      }`}
                    >

                      <div className="flex items-center justify-between mb-2.5">
                        <Icon className={`w-5 h-5 ${conf.color}`} />
                        {isUploaded && <CheckCircle className="w-4 h-4 text-amber-500 dark:text-lime-400" />}
                      </div>

                      <p className="text-xs font-semibold text-slate-800 dark:text-slate-100 truncate">{subKey}</p>

                      <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                        {isUploaded ? `${count} Chapters Loaded` : 'No JSON Uploaded'}
                      </p>

                    </div>

                  );

                })}

                <button
                  type="button"
                  onClick={() => { setNewSubjectName(''); setShowAddSubject(true); }}
                  className="p-4 rounded-2xl border border-dashed border-amber-300 dark:border-white/15 bg-transparent text-left transition-all hover:border-amber-500/50 hover:bg-amber-500/[0.05] dark:hover:border-lime-400/40 dark:hover:bg-lime-400/[0.04]"
                >
                  <div className="flex items-center justify-between mb-2.5">
                    <span className="grid h-5 w-5 place-items-center rounded-md bg-amber-500/15 text-amber-600 dark:bg-lime-400/15 dark:text-lime-300 text-sm leading-none">+</span>
                  </div>
                  <p className="text-xs font-semibold text-slate-800 dark:text-slate-100">Add Subject</p>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">Any board or exam subject</p>
                </button>

              </div>

              <div className="border border-dashed border-amber-300/80 dark:border-white/12 hover:border-amber-500/50 dark:hover:border-lime-400/35 rounded-2xl p-6 text-center transition bg-white/60 dark:bg-white/[0.02]">

                <Upload className="mx-auto w-7 h-7 text-amber-500 dark:text-lime-400 mb-2.5" />

                <h3 className="text-xs font-semibold text-slate-800 dark:text-slate-100">
                  Upload {activeUploadSubject} Syllabus JSON
                </h3>

                <p className="text-[11px] text-slate-500 dark:text-slate-400 max-w-sm mx-auto mt-1 mb-3.5">
                  Upload JSON for {activeUploadSubject} (Class {selectedClass}).
                </p>

                <label className="cursor-pointer px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold text-xs inline-block shadow-[0_8px_24px_-10px_rgba(245,158,11,0.8)] transition hover:opacity-90 dark:from-lime-400 dark:to-emerald-400 dark:text-[#0a1206] dark:shadow-[0_8px_24px_-10px_rgba(154,205,75,0.8)]">
                  Browse {activeUploadSubject} File
                  <input type="file" accept=".json" onChange={handleFileUpload} className="hidden" />
                </label>

              </div>

              {uploadError && (

                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-2 text-red-600 dark:text-red-400 text-xs font-medium">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{uploadError}</span>
                </div>

              )}

            </div>

            <button
              disabled={Object.keys(subjectPlanners).length === 0}
              onClick={() => setStep(2)}
              className="w-full py-4 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold text-xs transition shadow-[0_10px_30px_-12px_rgba(245,158,11,0.9)] disabled:opacity-40 disabled:shadow-none flex items-center justify-center gap-2 dark:from-lime-400 dark:to-emerald-400 dark:text-[#0a1206] dark:shadow-[0_10px_30px_-12px_rgba(154,205,75,0.9)]"
            >
              Continue to Backlog Selection <ArrowRight className="w-4 h-4" />
            </button>

            {/* TUTORIAL GUIDE: Strictly renders ONLY on Step 1 */}

            <TrackerTutorialGuide />

          </div>

        )}


        {/* STEP 2: Multi-Subject Chapter Backlog Selection */}

        {step === 2 && (

          <div className="p-6 rounded-2xl bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl border border-slate-200 dark:border-slate-800 space-y-5">

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-200 dark:border-slate-800 pb-4">

              <div>

                <h2 className="text-base font-bold">Select Backlog Chapters</h2>

                <p className="text-xs text-slate-500">Tick topics you need to complete</p>

              </div>

              <span className="text-xs bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:bg-cyan-500/10 dark:border-cyan-500/30 dark:text-cyan-400 px-3 py-1.5 rounded-full font-bold self-start">

                {selectedChapters.length} Chapters • {totalLecturesCount} Total Lectures

              </span>

            </div>

            {(() => {
              const subjectKeys = Object.keys(subjectPlanners);
              if (subjectKeys.length === 0) return null;
              const activeSubject =
                activeChapterSubject && subjectPlanners[activeChapterSubject]
                  ? activeChapterSubject
                  : subjectKeys[0];
              const chapters = subjectPlanners[activeSubject] || [];
              const selectedIn = (list) =>
                list.filter((c) => selectedChapters.some((sc) => sc.id === c.id)).length;
              const hasAll = chapters.length > 0 && selectedIn(chapters) === chapters.length;

              return (
                <div className="space-y-4">
                  {subjectKeys.length > 1 && (
                    <div className="-mx-1 px-1 overflow-x-auto no-scrollbar">
                      <div className="flex gap-2 min-w-max pb-1">
                        {subjectKeys.map((subject) => {
                          const list = subjectPlanners[subject] || [];
                          const isActive = subject === activeSubject;
                          return (
                            <button
                              key={subject}
                              onClick={() => setActiveChapterSubject(subject)}
                              className={`px-4 py-2.5 rounded-xl border text-left transition ${
                                isActive
                                  ? 'bg-gradient-to-r from-amber-500/20 to-orange-400/10 border-amber-500/70 text-slate-900 dark:from-lime-400/20 dark:to-emerald-400/10 dark:border-lime-400/70 dark:text-white shadow-[0_8px_24px_-14px_rgba(245,158,11,0.9)] dark:shadow-[0_8px_24px_-14px_rgba(154,205,75,0.9)]'
                                  : 'bg-white/70 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700/60 text-slate-600 dark:text-slate-300'
                              }`}
                            >
                              <span className="block text-xs font-bold whitespace-nowrap">
                                {subject}
                                {isActive && <span className="ml-1 text-amber-600 dark:text-lime-500">✓</span>}
                              </span>
                              <span className="block text-[10px] mt-0.5 text-slate-500 dark:text-slate-400 whitespace-nowrap">
                                {selectedIn(list)} / {list.length} selected
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <div>
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-md border ${subjectMeta(activeSubject).badge}`}>
                        {activeSubject}
                      </span>
                      <p className="text-[11px] text-slate-500 mt-1.5">{chapters.length} Chapters</p>
                    </div>

                    <button
                      onClick={() => {
                        const subChapterIds = new Set(chapters.map((c) => c.id));
                        if (hasAll) {
                          setSelectedChapters(selectedChapters.filter((c) => !subChapterIds.has(c.id)));
                        } else {
                          const toAdd = chapters.filter((c) => !selectedChapters.some((sc) => sc.id === c.id));
                          setSelectedChapters([...selectedChapters, ...toAdd]);
                        }
                      }}
                      className="text-[11px] font-semibold text-slate-500 hover:text-amber-600 dark:hover:text-lime-500 transition"
                    >
                      Toggle All {activeSubject}
                    </button>
                  </div>

                  <div key={activeSubject} className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[440px] overflow-y-auto pr-1">
                    {chapters.map((ch) => {
                      const isChecked = selectedChapters.some((c) => c.id === ch.id);
                      return (
                        <div
                          key={ch.id}
                          onClick={() => toggleChapter(ch)}
                          className={`p-3 rounded-xl border text-xs cursor-pointer transition flex items-center justify-between ${
                            isChecked
                              ? 'bg-amber-500/10 border-amber-500 text-slate-900 dark:bg-cyan-500/10 dark:border-cyan-500 dark:text-white shadow-sm'
                              : 'bg-white dark:bg-slate-800/70 border-slate-200 dark:border-slate-700/60 text-slate-600 dark:text-slate-300'
                          }`}
                        >
                          <div className="pr-2">
                            <p className="font-bold leading-snug">{ch.name}</p>
                            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">{ch.totalLectures} Lectures</p>
                          </div>

                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => {}}
                            className="accent-amber-500 dark:accent-cyan-500 w-4 h-4 flex-shrink-0"
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}


            <div className="flex gap-3 pt-2">

              <button

                onClick={() => setStep(1)}

                className="py-3 px-5 bg-slate-200 dark:bg-slate-800 rounded-xl font-bold text-xs transition"

              >

                Back

              </button>

              <button

                disabled={selectedChapters.length === 0}

                onClick={() => setStep(3)}

                className="flex-1 py-3 bg-[image:var(--gradient-brand)] disabled:opacity-40 text-primary-foreground font-bold rounded-xl text-xs transition flex items-center justify-center gap-2"

              >

                Configure Timeline & Speed ({totalLecturesCount} Lectures) <ArrowRight className="w-4 h-4" />

              </button>

            </div>

          </div>

        )}

        {/* STEP 3: 4-Year Slider & Timeline Calculator */}

        {step === 3 && (

          <div className="p-6 rounded-2xl bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl border border-slate-200 dark:border-slate-800 space-y-6">

            <div>

              <h2 className="text-base font-bold">Configure Target Timeline</h2>

              <p className="text-xs text-slate-500">System automatically calculates daily required lecture count</p>

            </div>

            {/* Extended 4-Year Target Days Slider */}

            <div className="p-5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 space-y-3">

              <div className="flex items-center justify-between">

                <label className="text-xs font-bold text-slate-700 dark:text-slate-200">

                  Target Completion Window

                </label>

                <div className="flex items-baseline gap-1.5">

                  <span className="text-sm font-black text-amber-600 dark:text-cyan-400">

                    {targetDays} Days

                  </span>

                  <span className="text-[11px] text-slate-500 font-semibold">

                    ({(targetDays / 365).toFixed(1)} {targetDays >= 365 ? 'Years' : 'Months'})

                  </span>

                </div>

              </div>

              <input

                type="range"

                min="15"

                max="1460"

                step="5"

                value={targetDays}

                onChange={(e) => setTargetDays(Number(e.target.value))}

                className="w-full accent-amber-500 dark:accent-cyan-500 cursor-pointer"

              />

              <div className="flex justify-between text-[11px] text-slate-400 font-medium">

                <span>15 Days</span>

                <span>1 Year (365d)</span>

                <span>2 Years (730d)</span>

                <span>4 Years (1460d)</span>

              </div>

            </div>

            {/* Schedule Frequency */}

            <div className="p-5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 space-y-3">

              <div>

                <label className="text-xs font-bold text-slate-700 dark:text-slate-200">Schedule Frequency</label>

                <p className="text-[11px] text-slate-500 mt-0.5">Choose how often lectures should be scheduled.</p>

              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">

                {[

                  { value: 1, label: 'Every Day', hint: 'Day 1, 2, 3, 4…' },

                  { value: 2, label: 'Every 2nd Day', hint: '1 day gap' },

                  { value: 3, label: 'Every 3rd Day', hint: '2 day gap' },

                  { value: 4, label: 'Every 4th Day', hint: '3 day gap' },

                ].map((opt) => (

                  <button

                    key={opt.value}

                    type="button"

                    onClick={() => setFrequency(opt.value)}

                    aria-pressed={frequency === opt.value}

                    className={`px-3 py-3 rounded-xl border text-left transition ${

                      frequency === opt.value

                        ? 'border-primary bg-primary/15 shadow-[var(--shadow-glow)]'

                        : 'border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-900/40'

                    }`}

                  >

                    <span className="block text-[11px] font-bold text-slate-800 dark:text-slate-100">{opt.label}</span>

                    <span className="block text-[10px] text-slate-500 mt-0.5">{opt.hint}</span>

                  </button>

                ))}

              </div>

              <div className="flex flex-wrap gap-2 pt-1">

                <span className="px-3 py-1.5 rounded-lg bg-white/70 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 text-[11px] font-semibold text-slate-700 dark:text-slate-200">

                  {effectiveStudyDays} scheduled study days

                </span>

                <span className="px-3 py-1.5 rounded-lg bg-white/70 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 text-[11px] font-semibold text-slate-700 dark:text-slate-200">

                  {calculatedDailyRate} lectures per study day

                </span>

              </div>

              {frequencyOverloaded && (

                <div className="p-3 rounded-xl border border-warning/40 bg-warning/10 text-[11px] font-medium text-warning">

                  This frequency packs {calculatedDailyRate} lectures (~{Math.round((calculatedDailyRate * lectureMinutes) / 60)}h) into every study day, which is more than a realistic day allows. Increase the target completion window or pick a more frequent schedule.

                </div>

              )}

            </div>

            {/* Daily Requirement Insight Box */}


            <div className="p-5 rounded-2xl bg-primary/10 border border-primary/20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">

              <div>

                <p className="text-xs font-bold text-amber-700 dark:text-cyan-300">Required Daily Pacing</p>

                <p className="text-xs text-slate-500 mt-0.5">

                  To finish {totalLecturesCount} lectures in {targetDays} days (excluding Sundays & Tests):

                </p>

              </div>

              <div className="px-4 py-2 rounded-xl bg-amber-500 dark:bg-cyan-500 text-white font-black text-base shadow-md shadow-amber-500/30 dark:shadow-cyan-500/30">

                {calculatedDailyRate} Lecs / Day

              </div>

            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

              <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30">

                <label className="text-xs font-bold text-slate-700 dark:text-slate-200 block mb-2">Lecture Playback Speed</label>

                <select

                  value={playbackSpeed}

                  onChange={(e) => setPlaybackSpeed(Number(e.target.value))}

                  className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-2.5 text-xs text-slate-800 dark:text-slate-200 font-medium outline-none"

                >

                  <option value={1.0}>1.0x (120m per lecture)</option>

                  <option value={1.25}>1.25x (96m per lecture)</option>

                  <option value={1.5}>1.5x (80m per lecture)</option>

                  <option value={1.75}>1.75x (68m per lecture)</option>

                  <option value={2.0}>2.0x (60m per lecture)</option>

                </select>

              </div>

              <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30">

                <label className="text-xs font-bold text-slate-700 dark:text-slate-200 block mb-2">Start Roadmap From</label>

                <input

                  type="date"

                  value={startDate}

                  onChange={(e) => setStartDate(e.target.value)}

                  className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-2.5 text-xs text-slate-800 dark:text-slate-200 font-medium outline-none"

                />

              </div>

            </div>

            <div className="flex gap-3">

              <button

                onClick={() => setStep(2)}

                className="py-3 px-5 bg-slate-200 dark:bg-slate-800 rounded-xl font-bold text-xs transition"

              >

                Back

              </button>

              <button

                onClick={buildSchedule}

                className="flex-1 py-4 bg-[image:var(--gradient-brand)] text-primary-foreground font-bold rounded-xl text-xs transition shadow-[var(--shadow-glow)]"

              >

                Generate Day-by-Day Master Timetable

              </button>

            </div>

          </div>

        )}

        {/* STEP 5: Master Daily Schedule Timeline */}

        {step === 5 && (

          <div className="space-y-4">

            {showPendingBanner && (
              <YesterdayPendingBanner
                date={yesterday}
                lectures={yesterdayPending}
                onDone={markYesterdayDone}
                onMoveToToday={(lec) => openMoveModal(lec, yesterday, todayISO())}
                onChooseDate={(lec) => openMoveModal(lec, yesterday)}
                onDismiss={dismissPending}
              />
            )}

            <div className="space-y-3">

              {(isPremiumUser ? visibleSchedule : visibleSchedule.filter((d) => d.dayNumber <= 2)).map((day) => (

                day.type === 'REVISION' ? (

                  <SundayRevisionCard
                    key={day.dayNumber}
                    date={prettyDate(day.date)}
                    checks={sundayChecks[day.date]}
                    onToggle={(key) => toggleSundayCheck(day.date, key)}
                  />

                ) : (

                <div

                  key={day.dayNumber}

                  className={`p-5 rounded-2xl border backdrop-blur-xl transition-all ${

                    day.type === 'TEST'

                      ? 'bg-primary/10 border-primary/40 shadow-sm'

                      : day.type === 'REVISION'

                      ? 'bg-amber-500/5 dark:bg-amber-950/20 border-amber-500/40 shadow-sm'

                      : 'bg-white/80 dark:bg-slate-900/60 border-slate-200 dark:border-slate-800 shadow-sm'

                  }`}

                >

                  <div className="flex items-center justify-between mb-3">

                    <div className="flex items-center gap-2.5">

                      <span className="text-xs font-black px-2.5 py-1 bg-slate-100 dark:bg-slate-800 rounded-lg text-slate-700 dark:text-slate-300">

                        Day {day.visibleIdx}

                      </span>

                      <span className="text-xs text-slate-500 font-medium">{day.date}</span>

                    </div>

                    <span

                      className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full ${

                        day.isOff 

                          ? 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/20' 

                          : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'

                      }`}

                    >

                      {day.isOff ? 'Zero Lectures' : `${day.lectures.length} Lectures`}

                    </span>

                  </div>

                  <h4 className="font-bold text-sm text-slate-800 dark:text-slate-100 mb-2">{day.title}</h4>

                  {day.isOff ? (

                    <p className="text-xs text-slate-500 dark:text-slate-400">{day.description}</p>

                  ) : (

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 mt-3">

                      {day.lectures.map((lec) => {

                        const badgeStyle = subjectMeta(lec.subject).badge;

                        const isLockedForFree = !isPremiumUser && day.dayNumber > 2 && !lec.completed;
                        const isFutureDay = !lec.completed && !!day.date && day.date > todayISO();
                        const isMissedDay = !lec.completed && !!day.date && day.date < todayISO();

                        return (

                          <div

                            key={lec.id}

                            className={`p-3.5 rounded-xl border flex items-center justify-between gap-3 transition-all ${

                              lec.completed

                                ? 'bg-emerald-500/5 border-emerald-500/40 opacity-70'

                                : isLockedForFree

                                ? 'bg-slate-100/50 dark:bg-slate-800/20 border-slate-200/80 dark:border-slate-800'

                                : 'bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700/60'

                            }`}

                          >

                            <div className="pr-1 overflow-hidden">

                              <div className="flex items-center gap-2 mb-1">

                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${badgeStyle}`}>

                                  {lec.subject}

                                </span>

                                <span className="text-[11px] text-slate-400 font-medium truncate">

                                  Lec {lec.lectureNumber}/{lec.totalLecturesInChapter}

                                </span>

                              </div>

                              <p className={`text-xs font-bold leading-tight truncate ${lec.completed ? 'line-through text-slate-400' : 'text-slate-800 dark:text-slate-100'}`}>

                                {lec.chapterName}

                              </p>

                              <div className="flex items-center gap-3 mt-2 text-[11px] text-slate-500 dark:text-slate-400 font-medium">

                                <span className="flex items-center gap-1">

                                  <Clock className="w-3 h-3 text-amber-500 dark:text-cyan-500" /> {lec.effectiveDuration}m

                                </span>

                                <span className="flex items-center gap-1">

                                  <FileText className="w-3 h-3 text-primary" /> {lec.dpp}

                                </span>

                              </div>

                            </div>

                            <div className="flex flex-shrink-0 flex-col items-stretch gap-1.5">

                            {isFutureDay && !isLockedForFree ? (
                              <>
                                <button
                                  disabled
                                  aria-disabled="true"
                                  title={`Mark available on ${prettyDate(day.date)}`}
                                  className="px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 flex-shrink-0 bg-slate-200 dark:bg-slate-700 text-slate-400 dark:text-slate-500 opacity-60 cursor-not-allowed"
                                >
                                  <Lock className="w-3.5 h-3.5" /> Mark
                                </button>
                                <span className="text-[10px] font-medium text-slate-500 dark:text-slate-400 text-center">🔒 Mark available on {prettyDate(day.date)}</span>
                              </>
                            ) : (
                            <button

                              onClick={() => toggleLectureStatus(day.originalIdx, lec.id, day.visibleIdx)}

                              className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition flex-shrink-0 ${

                                lec.completed

                                  ? 'bg-emerald-600 text-white shadow-sm'

                                  : isLockedForFree

                                  ? 'bg-amber-500/10 border border-amber-500/30 text-amber-600 dark:text-amber-400 hover:bg-amber-500/20'

                                  : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-amber-500 dark:hover:bg-cyan-500 hover:text-white'

                              }`}

                            >

                              {isLockedForFree ? (

                                <>

                                  <Lock className="w-3.5 h-3.5" /> Lock

                                </>

                              ) : (

                                <>

                                  <CheckSquare className="w-3.5 h-3.5" /> {lec.completed ? 'Done' : 'Mark'}

                                </>

                              )}

                            </button>
                            )}

                            {!lec.completed && !isLockedForFree && (
                              <button
                                onClick={() => openMoveModal(lec, day.date)}
                                className="flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-1.5 text-[11px] font-bold text-slate-500 dark:text-slate-400 transition hover:border-primary/50 hover:text-primary"
                              >
                                <CalendarClock className="w-3.5 h-3.5" /> Move
                              </button>
                            )}

                            </div>

                          </div>

                        );

                      })}

                    </div>

                  )}

                </div>

                )

              ))}

              {!isPremiumUser && visibleSchedule.some((d) => d.dayNumber > 2) && (
                <div className="relative overflow-hidden p-6 rounded-2xl border border-primary/40 bg-primary/5 dark:bg-primary/10 backdrop-blur-xl text-center shadow-sm">
                  <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-[image:var(--gradient-brand)] text-primary-foreground">
                    <Lock className="w-5 h-5" />
                  </div>
                  <h4 className="text-sm font-black text-slate-800 dark:text-slate-100">
                    Your complete preparation schedule is ready!
                  </h4>
                  <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
                    Unlock Day 8 to Exam Day with Pro.
                  </p>
                  <button
                    onClick={() => openTracker360Premium(() => openUpgrade(TRACKER_360_PREMIUM_POPUP.description))}
                    className="mt-4 inline-flex items-center justify-center gap-2 rounded-xl bg-[image:var(--gradient-brand)] px-5 py-3 text-[11px] font-black text-primary-foreground shadow-[0_10px_30px_-12px_rgba(154,205,75,0.9)]"
                  >
                    <Crown className="w-3.5 h-3.5" /> Unlock Full Schedule with Pro
                  </button>
                </div>
              )}

            </div>


          </div>

        )}


        {/* Add Custom Subject Modal */}

        {showAddSubject && typeof document !== 'undefined' && createPortal(

          <div
            className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={() => setShowAddSubject(false)}
          >

            <div
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-sm p-6 rounded-3xl bg-[var(--background)] border border-white/10 shadow-[0_0_60px_-20px_rgba(154,205,75,0.35)] text-[var(--foreground)]"
            >

              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-base font-bold">Add Subject</h3>
                  <p className="text-[11px] text-[var(--muted-foreground)] mt-1">
                    English, Computer Science, Economics — any subject you study.
                  </p>
                </div>
                <button onClick={() => setShowAddSubject(false)} className="p-1.5 rounded-lg text-[var(--muted-foreground)] hover:text-white hover:bg-white/5">
                  <X className="w-4 h-4" />
                </button>
              </div>

              <input
                autoFocus
                value={newSubjectName}
                onChange={(e) => setNewSubjectName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAddSubject(); }}
                placeholder="Subject name"
                className="mt-5 w-full px-4 py-3 rounded-2xl bg-white/[0.03] border border-white/10 text-sm text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] outline-none focus:border-amber-500/50 dark:focus:border-lime-400/40"
              />

              <button
                onClick={handleAddSubject}
                disabled={!newSubjectName.trim()}
                className="mt-4 w-full py-3.5 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 text-white dark:from-lime-400 dark:to-emerald-400 dark:text-[#0a1206] text-xs font-semibold shadow-[0_10px_30px_-12px_rgba(245,158,11,0.9)] dark:shadow-[0_10px_30px_-12px_rgba(154,205,75,0.9)] disabled:opacity-40 disabled:shadow-none transition"
              >
                Add Subject
              </button>

            </div>

          </div>,

          document.body

        )}

        {/* Lecture move / reschedule modal */}

        <LectureMoveModal
          open={!!moveTarget}
          lecture={moveTarget?.lecture}
          fromDate={moveTarget?.fromDate}
          preselectDate={moveTarget?.preselectDate}
          schedule={generatedSchedule}
          onClose={() => setMoveTarget(null)}
          onConfirm={confirmMove}
        />

        {limitNotice && typeof document !== 'undefined' && createPortal(
          <div
            className="fixed inset-0 z-[130] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
            onClick={() => setLimitNotice('')}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-xs rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 text-center shadow-2xl"
            >
              <p className="text-xs font-semibold leading-relaxed text-slate-700 dark:text-slate-200">{limitNotice}</p>
              <button
                onClick={() => setLimitNotice('')}
                className="mt-4 w-full rounded-xl bg-[image:var(--gradient-brand)] py-2.5 text-[11px] font-black text-primary-foreground"
              >
                Got it
              </button>
            </div>
          </div>,
          document.body,
        )}

        {/* Tracker 360 Premium Modal */}




        {showPaywallModal && typeof document !== 'undefined' && createPortal(

          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto">

            <div className="relative w-full max-w-md my-auto p-6 sm:p-7 rounded-[32px] bg-popover border border-border shadow-2xl text-foreground animate-in fade-in zoom-in-95 duration-150">

              {/* Close Button */}

              <button 

                onClick={() => setShowPaywallModal(false)}

                className="absolute top-6 right-6 w-9 h-9 flex items-center justify-center rounded-full bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600 transition"

              >

                <X className="w-5 h-5" />

              </button>

              {/* Header */}

              <div className="space-y-2 pr-10">

                <div className="inline-flex items-center gap-1.5 text-xs font-bold text-primary uppercase tracking-wider px-2.5 py-1 rounded-full bg-primary/10 border border-primary/20">

                  <Sparkles className="w-3.5 h-3.5" /> {TRACKER_360_PREMIUM_POPUP.badge}

                </div>

                <h2 className="text-2xl font-black tracking-tight text-slate-900 dark:text-white">

                  {TRACKER_360_PREMIUM_POPUP.title}

                </h2>

                <p className="text-xs text-slate-500 dark:text-slate-400 font-medium leading-relaxed">

                  {paywallReason || TRACKER_360_PREMIUM_POPUP.description}

                </p>

              </div>

              {/* Benefits Checklist */}

              <div className="my-5 space-y-2.5 text-xs font-medium text-slate-700 dark:text-slate-300">

                {TRACKER_360_PREMIUM_POPUP.benefits.map((feat, i) => (

                  <div key={i} className="flex items-center gap-2.5">

                    <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />

                    <span>{feat}</span>

                  </div>

                ))}

              </div>

              {/* CTA Buttons */}

              <div className="space-y-3">

                <button

                  onClick={() => {

                    setShowPaywallModal(false);

                    openTracker360Premium(() => openUpgrade(TRACKER_360_PREMIUM_POPUP.description));

                  }}

                  className="w-full flex items-center justify-center gap-2 px-4 py-3.5 rounded-2xl bg-[image:var(--gradient-brand)] text-primary-foreground text-sm font-black shadow-[var(--shadow-glow)] hover:opacity-90 transition"

                >

                  <Crown className="w-4 h-4" />

                  {TRACKER_360_PREMIUM_POPUP.primaryButton}

                </button>

                <button

                  onClick={() => setShowPaywallModal(false)}

                  className="w-full px-4 py-3 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 text-sm font-bold hover:bg-slate-50 dark:hover:bg-slate-700 transition"

                >

                  {TRACKER_360_PREMIUM_POPUP.secondaryButton}

                </button>

              </div>

              {/* Free Info */}

              <div className="mt-4 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">

                <p className="text-[11px] font-semibold text-amber-700 dark:text-amber-400">

                  {TRACKER_360_FREE_INFO.title}: <span className="font-bold">{TRACKER_360_FREE_INFO.unlockedDays}</span>

                </p>

                <p className="text-[10px] text-amber-600/80 dark:text-amber-400/80 mt-1">

                  {TRACKER_360_FREE_INFO.message}

                </p>

              </div>

            </div>

          </div>,

          document.body

        )}

      </div>

    </div>

  );

}